import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import styles from '../ReportMetrics.module.scss';
import { usePortfolioIntelligence } from '../../../hooks/usePortfolioIntelligence';
import { formatPercent } from '../intelligence';
import {
  PortfolioRow,
  needsAttention,
  oldestComputedAt,
  portfolioHistoryPoints,
  portfolioRows,
  portfolioSummary,
  weakestDimension,
} from '../portfolio';
import { TONE_COLOR, scoreBand, signed, directionOf } from '../detail';
import { CategoryDatum, KpiDatum, StatusDatum } from '../reportData';
import type { ReportViewProps } from './viewProps';
import type { ReportTab } from '../ReportMetrics';
import KpiCard from '../widgets/KpiCard';
import ChartCard from '../widgets/ChartCard';
import CategoryBarChart from '../widgets/CategoryBarChart';
import KpiHistoryChart from '../widgets/KpiHistoryChart';
import DataTable from '../widgets/DataTable';
import StatusDonut from '../widgets/StatusDonut';
import detailStyles from '../widgets/Detail.module.scss';
import PortfolioHeatmap from '../widgets/PortfolioHeatmap';
import LastComputed from '../widgets/LastComputed';
import { linkInClickableParent } from '../../../utils/linkClick';

/** How many projects "Projekte mit Handlungsbedarf" lists, as in the spec's "Top 5". */
const ATTENTION_LIMIT = 5;

/**
 * Portfolio Overview (Detailmasken §8, which superseded spec §17): Portfolio
 * Summary, then Health Trend · Health distribution · Projekte mit Handlungsbedarf
 * in one row and Heatmap · Actions by project in the next. The Health & Attention
 * Matrix was dropped on the customer's word (2026-10-20).
 *
 * Unlike the other three views this one fetches its own data rather than taking
 * it from the shell's props: a portfolio is not about the open project, and
 * making the shell load it would mean every visit to the Executive Summary
 * pulled the whole portfolio down with it.
 *
 * Because it needs none of those props, it also stands on its own outside the
 * report shell - which is how the workspace-level Project Intelligence page
 * renders it, without a project being open at all.
 */
const Portfolio: React.FC<Partial<ReportViewProps>> = () => {
  const { t } = useTranslation('templates');
  const { projects, healthHistory, isLoading, isError } = usePortfolioIntelligence();

  const rows = useMemo(() => portfolioRows(projects), [projects]);
  const summary = useMemo(() => portfolioSummary(rows), [rows]);
  const navigate = useNavigate();
  const attention = useMemo(() => needsAttention(rows, ATTENTION_LIMIT), [rows]);
  const distribution: StatusDatum[] = useMemo(
    () =>
      (['onTrack', 'watch', 'atRisk', 'critical'] as const)
        .map((key) => ({
          status: `health_${key}`,
          value: summary.byHealth[key],
          color: TONE_COLOR[scoreBand({ onTrack: 90, watch: 70, atRisk: 50, critical: 10 }[key])!.tone],
        }))
        .filter((d) => d.value > 0),
    [summary],
  );
  const openProject = (row: PortfolioRow, tab: ReportTab = 'overview') =>
    void navigate({
      to: '/project/$projectId',
      params: { projectId: String(row.id) },
      search: { section: 'report-metrics', report: tab },
    });
  const projectHref = (row: PortfolioRow, tab: ReportTab = 'overview') =>
    `/project/${row.id}?section=report-metrics&report=${tab}`;
  // the stalest project on the page, since that is how current the page really is
  const computedAt = useMemo(() => oldestComputedAt(rows), [rows]);

  const alertsByProject: CategoryDatum[] = useMemo(
    () =>
      [...rows]
        .filter((row) => row.openAlerts > 0)
        .sort((a, b) => b.openAlerts - a.openAlerts)
        .map((row) => ({ label: row.name, value: row.openAlerts })),
    [rows],
  );

  const trendPoints = useMemo(
    () => portfolioHistoryPoints(healthHistory?.[0]),
    [healthHistory],
  );

  const tiles: KpiDatum[] = useMemo(
    () => [
      {
        key: 'totalProjects',
        value: `${summary.total}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: true,
      },
      {
        key: 'averageHealth',
        value: formatPercent(summary.averageHealth),
        delta: '—',
        direction: 'flat',
        higherIsBetter: true,
      },
      {
        key: 'onTrack',
        value: `${summary.byHealth.onTrack}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: true,
      },
      {
        key: 'watch',
        value: `${summary.byHealth.watch}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: false,
      },
      {
        key: 'atRisk',
        value: `${summary.byHealth.atRisk}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: false,
      },
      {
        key: 'critical',
        value: `${summary.byHealth.critical}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: false,
      },
      {
        key: 'openAlerts',
        value: `${summary.openAlerts}`,
        delta: '—',
        direction: 'flat',
        higherIsBetter: false,
      },
    ],
    [summary],
  );

  if (isLoading) {
    return (
      <div className={styles.dashboard}>
        <p className={styles.planDescription}>{t('report.state.loading')}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.dashboard}>
        <p className={styles.planDescription}>{t('report.state.error')}</p>
      </div>
    );
  }

  // No visible project at all is a legitimate state (a user who is a member of
  // none), and distinct from "projects exist but have no KPIs yet".
  if (rows.length === 0) {
    return (
      <div className={styles.dashboard}>
        <p className={styles.planDescription}>{t('report.portfolio.empty')}</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.freshnessRow}>
        <LastComputed at={computedAt} scope="portfolio" />
      </div>

      <div className={styles.tileRow}>
        {tiles.map((datum) => (
          <KpiCard
            key={datum.key}
            label={t(`report.portfolio.tiles.${datum.key}.label`)}
            hint={t(`report.portfolio.tiles.${datum.key}.hint`)}
            datum={datum}
          />
        ))}
      </div>

      <div className={styles.chartGrid}>
        <ChartCard
          title={t('report.portfolio.trend.title')}
          subtitle={t('report.portfolio.trend.subtitle')}
        >
          {trendPoints.length > 0 ? (
            <KpiHistoryChart
              data={trendPoints}
              seriesName={t('report.portfolio.trend.seriesName')}
              max={100}
            />
          ) : (
            <p className={styles.planDescription}>{t('report.trendScore.noHistory')}</p>
          )}
        </ChartCard>

        <ChartCard
          title={t('report.portfolio.distribution.title')}
          subtitle={t('report.portfolio.distribution.subtitle')}
        >
          {distribution.length > 0 ? (
            <StatusDonut data={distribution} totalLabel={t('report.portfolio.distribution.projects')} />
          ) : (
            <p className={styles.planDescription}>{t('report.portfolio.ranking.empty')}</p>
          )}
        </ChartCard>

        <ChartCard
          title={t('report.portfolio.attention.title', { count: ATTENTION_LIMIT })}
          subtitle={t('report.portfolio.attention.subtitle')}
          fitContent
        >
          <DataTable
            rows={attention}
            rowKey={(row) => row.id}
            empty={t('report.portfolio.attention.empty')}
            onRowClick={(row) => openProject(row)}
            columns={[
              {
                key: 'project',
                header: t('report.portfolio.heatmap.project'),
                render: (row) => (
                  <a className={detailStyles.rowLink} href={projectHref(row)} onClick={linkInClickableParent}>
                    {row.name}
                  </a>
                ),
              },
              {
                key: 'health',
                header: t('report.portfolio.dimensions.health'),
                align: 'right',
                render: (row) => (
                  <span style={{ color: TONE_COLOR[scoreBand(row.health)?.tone ?? 'neutral'], fontWeight: 600 }}>
                    {formatPercent(row.health)}
                  </span>
                ),
              },
              {
                key: 'trend',
                header: t('report.cockpit.trend'),
                align: 'right',
                render: (row) => {
                  if (typeof row.healthTrend !== 'number') return '—';
                  const direction = directionOf(row.healthTrend);
                  return (
                    <span className={direction === 'up' ? detailStyles.good : direction === 'down' ? detailStyles.bad : detailStyles.flat}>
                      {signed(row.healthTrend)}
                    </span>
                  );
                },
              },
              {
                key: 'driver',
                header: t('report.portfolio.attention.driver'),
                render: (row) => {
                  const dimension = weakestDimension(row);
                  return dimension ? t(`report.portfolio.dimensions.${dimension}`) : '—';
                },
              },
              {
                key: 'alerts',
                header: t('report.portfolio.attention.findings'),
                align: 'right',
                render: (row) => (
                  <span className={row.openAlerts > 0 ? detailStyles.critical : undefined}>{row.openAlerts}</span>
                ),
              },
            ]}
          />
        </ChartCard>

        <div className={styles.spanTwo}>
          <ChartCard
            title={t('report.portfolio.heatmap.title')}
            subtitle={t('report.portfolio.heatmap.subtitle')}
            fitContent
          >
            <PortfolioHeatmap rows={rows} onOpen={openProject} hrefFor={projectHref} />
          </ChartCard>
        </div>

        <ChartCard
          title={t('report.portfolio.alerts.title')}
          subtitle={t('report.portfolio.alerts.subtitle')}
        >
          {alertsByProject.length > 0 ? (
            <CategoryBarChart
              data={alertsByProject}
              seriesName={t('report.portfolio.alerts.seriesName')}
            />
          ) : (
            <p className={styles.planDescription}>{t('report.portfolio.alerts.empty')}</p>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default Portfolio;
